import { sanitizeBrandOverride } from './chunk-P4WSDWOY.js';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './chunk-Z2NUSCHM.js';
import { useOptionalTheme } from './chunk-EOFYHHEG.js';
import { NativeSelect } from './chunk-GSFYO3YU.js';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './chunk-PEIGN6MO.js';
import { useCopyToClipboard } from './chunk-TNAHBB5Q.js';
import { Input } from './chunk-NHXI3UAE.js';
import { Badge } from './chunk-2WDEGOCV.js';
import { Button } from './chunk-PLP233UC.js';
import { cn } from './chunk-FFX3LZW7.js';
import * as React from 'react';
import { coerceTenantContext, accessMatrix, personLabel, peopleIn, toMermaid } from '@authe/fe-commons/tenancy';
import { EMPTY_TENANCY_NAMES } from '@authe/fe-commons/auth';
import { jsx, jsxs } from 'react/jsx-runtime';

var TenantReactContext = React.createContext(null);
function TenantProvider({
  tenant,
  names,
  branding,
  brandingOptions,
  children
}) {
  const resolved = React.useMemo(() => coerceTenantContext(tenant), [tenant]);
  const theme = useOptionalTheme();
  const setBrand = theme?.setBrand;
  React.useEffect(() => {
    if (!setBrand || !branding) return;
    setBrand(sanitizeBrandOverride(branding, brandingOptions));
  }, [setBrand, branding, brandingOptions]);
  const value = React.useMemo(
    () => ({ tenant: resolved, names: names ?? EMPTY_TENANCY_NAMES }),
    [resolved, names]
  );
  return /* @__PURE__ */ jsx(TenantReactContext.Provider, { value, children });
}
function useTenant() {
  const ctx = React.useContext(TenantReactContext);
  if (!ctx) throw new Error("useTenant must be used inside <TenantProvider>");
  return ctx.tenant;
}
function useOptionalTenant() {
  return React.useContext(TenantReactContext)?.tenant ?? null;
}
function useTenancyNames() {
  return React.useContext(TenantReactContext)?.names ?? EMPTY_TENANCY_NAMES;
}
function useCurrentOrg() {
  const ctx = React.useContext(TenantReactContext);
  if (!ctx) throw new Error("useCurrentOrg must be used inside <TenantProvider>");
  const t = ctx.tenant;
  const names = ctx.names;
  return {
    orgId: t.orgId,
    tenantId: t.tenantId,
    orgIdTenantId: t.orgIdTenantId,
    customerId: t.customerId,
    isGlobal: t.isGlobal,
    hasCustomerScope: t.hasCustomerScope,
    orgName: names.orgName ?? t.orgId,
    tenantName: names.tenantName ?? t.tenantId,
    customerName: names.customerName ?? t.customerId
  };
}
function useUserScope() {
  const t = useTenant();
  return { userId: t.userId, isUserScoped: t.isUserScoped };
}
var plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
function TenancyStructureView({
  structure,
  initialPersonId = null,
  view,
  onViewChange,
  renderTenantActions,
  className
}) {
  const [personId, setPersonId] = React.useState(initialPersonId);
  const [localView, setLocalView] = React.useState("map");
  const changeView = (next) => {
    setLocalView(next);
    onViewChange?.(next);
  };
  const [openTenant, setOpenTenant] = React.useState(null);
  const matrix = React.useMemo(() => accessMatrix(structure), [structure]);
  const reach = React.useMemo(() => {
    const row = matrix.rows.find((r) => r.person.userId === personId);
    return row ? new Set(Object.keys(row.roles)) : null;
  }, [matrix, personId]);
  const tenantCount = matrix.columns.length;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), "data-testid": "tenancy-structure", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: structure.customer.name }),
        " \xB7 ",
        plural(structure.orgs.length, "organization", "organizations"),
        " \xB7 ",
        plural(tenantCount, "tenant", "tenants"),
        " \xB7",
        " ",
        plural(structure.people.length, "person", "people")
      ] }),
      /* @__PURE__ */ jsx(MermaidCopy, { structure })
    ] }),
    structure.hiddenOrgs > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", role: "note", children: [
      plural(structure.hiddenOrgs, "more organization", "more organizations"),
      " of this customer \u2014 you are not a member, so ",
      structure.hiddenOrgs === 1 ? "it is" : "they are",
      " not shown."
    ] }) : null,
    /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "People can have roles in several tenants. Tenant access does not expose another person\u2019s private copies." }),
    /* @__PURE__ */ jsxs(Tabs, { value: view ?? localView, onValueChange: (next) => changeView(next), children: [
      /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsx(TabsTrigger, { value: "map", children: "Map" }),
        /* @__PURE__ */ jsx(TabsTrigger, { value: "access", children: "Access" })
      ] }),
      /* @__PURE__ */ jsxs(TabsContent, { value: "map", className: "space-y-3", children: [
        /* @__PURE__ */ jsxs("label", { className: "flex max-w-lg flex-wrap items-center gap-2 text-sm", children: [
          /* @__PURE__ */ jsx("span", { className: "shrink-0 text-muted-foreground", children: "Show access for" }),
          /* @__PURE__ */ jsxs(
            NativeSelect,
            {
              density: "sm",
              "aria-label": "Show where this person holds a role",
              value: personId ?? "",
              onChange: (e) => setPersonId(e.target.value || null),
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Everyone (all tenants)" }),
                structure.people.map((p) => /* @__PURE__ */ jsxs("option", { value: p.userId, children: [
                  personLabel(p),
                  p.pending ? " (invited)" : ""
                ] }, p.userId))
              ]
            }
          )
        ] }),
        structure.orgs.map((org) => /* @__PURE__ */ jsxs("section", { className: "rounded-lg border p-3", "aria-label": `Organization ${org.name}`, children: [
          /* @__PURE__ */ jsxs("h3", { className: "mb-2 flex items-center gap-2 text-sm font-semibold", children: [
            org.name,
            org.isRoot ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "root org" }) : null
          ] }),
          /* @__PURE__ */ jsx("div", { className: "grid gap-2 sm:grid-cols-2 lg:grid-cols-3", children: org.tenants.map((tenant) => {
            const people = peopleIn(structure, tenant.id);
            const lit = reach ? reach.has(tenant.id) : null;
            const roles = reach ? matrix.rows.find((r) => r.person.userId === personId)?.roles[tenant.id] : void 0;
            return /* @__PURE__ */ jsxs(
              "div",
              {
                "data-tenant-id": tenant.id,
                "data-reached": lit === null ? void 0 : String(lit),
                className: cn(
                  "rounded-md border bg-card p-2 text-sm transition-opacity",
                  lit === true && "ring-2 ring-primary",
                  lit === false && "border-dashed"
                ),
                children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
                    /* @__PURE__ */ jsx("span", { className: "font-medium", children: tenant.name }),
                    /* @__PURE__ */ jsxs("span", { className: "flex flex-wrap items-center gap-1", children: [
                      tenant.isRoot ? /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "root" }) : null,
                      roles?.map((r) => /* @__PURE__ */ jsx(Badge, { variant: "info", children: r }, r)),
                      lit === false ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No role for this person" }) : null
                    ] })
                  ] }),
                  /* @__PURE__ */ jsxs(
                    "button",
                    {
                      type: "button",
                      className: "mt-1 text-xs text-muted-foreground underline-offset-2 hover:underline",
                      "aria-expanded": openTenant === tenant.id,
                      "aria-label": `Show people in ${org.name} / ${tenant.name}`,
                      onClick: () => setOpenTenant(openTenant === tenant.id ? null : tenant.id),
                      children: [
                        plural(people.length, "person", "people"),
                        " with a role here"
                      ]
                    }
                  ),
                  openTenant === tenant.id ? /* @__PURE__ */ jsx("ul", { className: "mt-1 space-y-0.5 text-xs", children: people.map(({ person, roles: held }) => /* @__PURE__ */ jsxs("li", { className: "flex justify-between gap-2", children: [
                    /* @__PURE__ */ jsxs("span", { className: "min-w-0 flex-1", children: [
                      /* @__PURE__ */ jsx(
                        "button",
                        {
                          type: "button",
                          className: "block max-w-full truncate text-left underline-offset-2 hover:underline",
                          "aria-label": `Show access for ${personLabel(person)}`,
                          onClick: () => setPersonId(person.userId),
                          children: personLabel(person)
                        }
                      ),
                      person.email && person.name ? /* @__PURE__ */ jsx("span", { className: "block truncate text-muted-foreground", children: person.email }) : null
                    ] }),
                    /* @__PURE__ */ jsx("span", { className: "max-w-[45%] truncate text-right text-muted-foreground", title: held.join(", "), children: held.join(", ") })
                  ] }, person.userId)) }) : null,
                  /* @__PURE__ */ jsx("div", { className: "mt-2 flex flex-wrap gap-1", children: tenant.teamsVisible ? tenant.teams.length ? tenant.teams.map((team) => /* @__PURE__ */ jsxs(Badge, { variant: "outline", title: team.isDefault ? "The tenant's default team" : void 0, children: [
                    team.name,
                    team.isDefault ? " \u2605" : "",
                    " \xB7 ",
                    team.members.length
                  ] }, team.id)) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No teams" }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Teams not shown: you hold no role here" }) }),
                  renderTenantActions ? /* @__PURE__ */ jsx("div", { className: "mt-2", children: renderTenantActions(org, tenant) }) : null
                ]
              },
              tenant.id
            );
          }) })
        ] }, org.id))
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "access", children: /* @__PURE__ */ jsx(AccessTable, { structure, onSelectPerson: (id) => {
        setPersonId(id);
        changeView("map");
      } }) })
    ] })
  ] });
}
function AccessTable({ structure, onSelectPerson }) {
  const [query, setQuery] = React.useState("");
  const matrix = React.useMemo(() => accessMatrix(structure), [structure]);
  const needle = query.trim().toLowerCase();
  const rows = needle ? matrix.rows.filter((r) => `${r.person.name ?? ""} ${r.person.email ?? ""}`.toLowerCase().includes(needle)) : matrix.rows;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx(
      Input,
      {
        type: "search",
        placeholder: "Filter people",
        "aria-label": "Filter people",
        className: "max-w-xs",
        value: query,
        onChange: (e) => setQuery(e.target.value)
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "overflow-x-auto rounded-md border", children: /* @__PURE__ */ jsxs(Table, { children: [
      /* @__PURE__ */ jsxs(TableHeader, { children: [
        /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsx(TableHead, { rowSpan: 2, className: "sticky left-0 bg-background", children: "Person" }),
          structure.orgs.map((org) => /* @__PURE__ */ jsx(TableHead, { colSpan: org.tenants.length, scope: "colgroup", className: "border-l text-center", children: org.name }, org.id))
        ] }),
        /* @__PURE__ */ jsx(TableRow, { children: matrix.columns.map((c) => /* @__PURE__ */ jsx(TableHead, { scope: "col", className: "border-l", children: c.tenantName }, c.tenantId)) })
      ] }),
      /* @__PURE__ */ jsxs(TableBody, { children: [
        rows.map((row) => /* @__PURE__ */ jsxs(TableRow, { children: [
          /* @__PURE__ */ jsxs(TableCell, { className: "sticky left-0 bg-background", children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "text-left font-medium underline-offset-2 hover:underline",
                "aria-label": `Show ${personLabel(row.person)} on map`,
                onClick: () => onSelectPerson(row.person.userId),
                children: personLabel(row.person)
              }
            ),
            row.person.email && row.person.name ? /* @__PURE__ */ jsx("div", { className: "text-xs text-muted-foreground", children: row.person.email }) : null,
            row.person.pending ? /* @__PURE__ */ jsx(Badge, { variant: "warning", className: "mt-1", children: "invited" }) : null
          ] }),
          matrix.columns.map((c) => {
            const roles = row.roles[c.tenantId] ?? [];
            const teams = row.teams[c.tenantId] ?? [];
            return /* @__PURE__ */ jsxs(TableCell, { className: "border-l align-top", "data-cell": `${row.person.userId}:${c.tenantId}`, children: [
              roles.length ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1", children: roles.map((r) => /* @__PURE__ */ jsx(Badge, { variant: "info", children: r }, r)) }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "No role" }),
              teams.length ? /* @__PURE__ */ jsx("div", { className: "mt-1 text-xs text-muted-foreground", children: teams.join(", ") }) : null
            ] }, c.tenantId);
          })
        ] }, row.person.userId)),
        rows.length === 0 ? /* @__PURE__ */ jsx(TableRow, { children: /* @__PURE__ */ jsx(TableCell, { colSpan: matrix.columns.length + 1, className: "py-6 text-center text-muted-foreground", children: needle ? "No people match your search." : "No people are listed." }) }) : null
      ] })
    ] }) })
  ] });
}
function MermaidCopy({ structure }) {
  const { copy, copied, failed } = useCopyToClipboard();
  const [withPeople, setWithPeople] = React.useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs", children: [
    /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1 text-muted-foreground", children: [
      /* @__PURE__ */ jsx("input", { type: "checkbox", checked: withPeople, onChange: (e) => setWithPeople(e.target.checked) }),
      "Include people in export"
    ] }),
    /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: () => void copy(toMermaid(structure, { people: withPeople })), children: copied ? "Copied" : failed ? "Copy failed" : "Copy as Mermaid" })
  ] });
}

export { TenancyStructureView, TenantProvider, useCurrentOrg, useOptionalTenant, useTenancyNames, useTenant, useUserScope };
//# sourceMappingURL=chunk-5PP7JV3X.js.map
//# sourceMappingURL=chunk-5PP7JV3X.js.map